import { Avatar, Box, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../../components/Header";
import { authFetch } from "../../Utils/apiClient";

function ViewChurchMember() {
  const { id } = useParams();
  const [memberDetails, setMemberDetails] = useState(null);

  useEffect(() => {
    const fetchMemberDetails = async () => {
      try {
        const response = await authFetch(`/user/getUserById/${id}`);
        if (!response.ok) {
          throw new Error("Failed to fetch church member details");
        }
        const memberData = await response.json();

        const {
          email,
          profileName,
          streetNumber,
          streetName,
          APT,
          city,
          state,
          country,
          zipCode,
          imageUrl,
          Role,
          createdAt,
          ChurchMember
        } = memberData.result;

        const formattedMemberDetails = {
          email,
          profileName,
          streetNumber,
          streetName,
          APT,
          city,
          state,
          country,
          zipCode,
          imageUrl,
          role: Role.role,
          createdAt,
          // Church Member specific information
          fullName: ChurchMember?.fullname,
          age: ChurchMember?.age,
          memberPhone: ChurchMember?.memberphone,
          membershipOption: ChurchMember?.membershipOption,
          dob: ChurchMember?.dob,
          gender: ChurchMember?.gender,
          maritalStatus: ChurchMember?.maritalStatus,
          occupation: ChurchMember?.occupation,
          spouseName: ChurchMember?.spouseName,
          guardianName: ChurchMember?.guardianName,
          guardianPhone: ChurchMember?.guardianPhone,
          liveWithParentOrNot: ChurchMember?.liveWithParentOrNot,
          dependentSelection: ChurchMember?.dependentselection,
          newbornSelection: ChurchMember?.newbornselection,
          socialSecurityNumber: ChurchMember?.socialSecurityNumber,
          // Worship Center information
          worshipCenter: ChurchMember?.worshipCenter?.church,
          worshipCenterAddress: ChurchMember?.worshipCenter?.address,
          worshipCenterEmail: ChurchMember?.worshipCenter?.email,
          worshipCenterPhone: ChurchMember?.worshipCenter?.tel,
          worshipCenterPastor: ChurchMember?.worshipCenter?.paster,
          // Dependents and Newborns
          dependents: ChurchMember?.Dependents || [],
          newborns: ChurchMember?.Newborns || []
        };

        setMemberDetails(formattedMemberDetails);
      } catch (error) {
        console.error("Error fetching church member details:", error);
      }
    };

    fetchMemberDetails();
  }, [id]);

  const renderDetails = () => {
    if (!memberDetails) return null;

    const basicDetails = [
      { label: "Profile Name", value: memberDetails.profileName },
      { label: "Full Name", value: memberDetails.fullName },
      { label: "Email", value: memberDetails.email },
      { label: "Role", value: memberDetails.role },
      { label: "Phone", value: memberDetails.memberPhone },
    ];

    const personalDetails = [
      { label: "Date of Birth", value: formatDate(memberDetails.dob) },
      { label: "Age", value: memberDetails.age || "Not specified" },
      { label: "Gender", value: memberDetails.gender },
      { label: "Marital Status", value: memberDetails.maritalStatus },
      { label: "Occupation", value: memberDetails.occupation },
      { label: "Spouse Name", value: memberDetails.spouseName || "Not applicable" },
      { label: "Membership Option", value: memberDetails.membershipOption },
    ];

    const guardianDetails = [
      { label: "Guardian Name", value: memberDetails.guardianName || "Not provided" },
      { label: "Guardian Phone", value: memberDetails.guardianPhone || "Not provided" },
      { label: "Lives with Parents", value: memberDetails.liveWithParentOrNot },
      { label: "Social Security Number", value: memberDetails.socialSecurityNumber || "Not provided" },
    ];

    const addressDetails = [
      { label: "Street Number", value: memberDetails.streetNumber },
      { label: "Street Name", value: memberDetails.streetName },
      { label: "APT", value: memberDetails.APT },
      { label: "City", value: memberDetails.city },
      { label: "State", value: memberDetails.state },
      { label: "Country", value: memberDetails.country },
      { label: "Zip Code", value: memberDetails.zipCode },
    ];

    const worshipCenterDetails = [
      { label: "Worship Center", value: memberDetails.worshipCenter },
      { label: "Worship Center Address", value: memberDetails.worshipCenterAddress },
      { label: "Worship Center Email", value: memberDetails.worshipCenterEmail },
      { label: "Worship Center Phone", value: memberDetails.worshipCenterPhone },
      { label: "Pastor", value: memberDetails.worshipCenterPastor },
    ];

    const systemDetails = [
      { label: "Created At", value: formatCreatedAt(memberDetails.createdAt) },
    ];

    // Combine all detail sections
    const allDetails = [
      ...basicDetails,
      ...personalDetails,
      ...guardianDetails,
      ...addressDetails,
      ...worshipCenterDetails,
      ...systemDetails
    ];

    return (
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={9}>
          {allDetails.map((item) => (
            <Grid container spacing={1} key={item.label} sx={{ marginBottom: 1 }}>
              <Grid item xs={6} md={3}>
                <Typography
                  variant="body1"
                  sx={{ fontSize: "1rem", marginBottom: 1 }}
                >
                  <strong>{item.label}</strong>
                </Typography>
              </Grid>
              <Grid item xs={6} md={9}>
                <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                  : {item.value || "Not provided"}
                </Typography>
              </Grid>
            </Grid>
          ))}
          
          {/* Dependents Section */}
          {memberDetails.dependents.length > 0 && memberDetails.dependents[0]?.name && (
            <Box mt={3}>
              <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: 2 }}>
                Dependents
              </Typography>
              {memberDetails.dependents.map((dependent, index) => (
                <Grid container spacing={1} key={dependent.id} sx={{ marginBottom: 1 }}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                      <strong>Dependent {index + 1}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={9}>
                    <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                      : {dependent.name || "Not specified"} 
                      {dependent.dob && `, DOB: ${formatDate(dependent.dob)}`}
                      {dependent.gender && `, Gender: ${dependent.gender}`}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Box>
          )}

          {/* Newborns Section */}
          {memberDetails.newborns.length > 0 && memberDetails.newborns[0]?.name && (
            <Box mt={3}>
              <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: 2 }}>
                Newborns
              </Typography>
              {memberDetails.newborns.map((newborn, index) => (
                <Grid container spacing={1} key={newborn.id} sx={{ marginBottom: 1 }}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                      <strong>Newborn {index + 1}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={9}>
                    <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                      : {newborn.name || "Not specified"}
                      {newborn.dob && `, DOB: ${formatDate(newborn.dob)}`}
                      {newborn.gender && `, Gender: ${newborn.gender}`}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Box>
          )}
        </Grid>
      </Grid>
    );
  };

  const formatCreatedAt = (createdAt) => {
    const date = new Date(createdAt);
    return date.toLocaleString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not provided";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Box p="20px">
      <Header
        title={`View Church Member ID ${id}`}
        subtitle={memberDetails ? `Role: ${memberDetails.role}` : ""}
      />
      <Box m="10px 0 0 0" height="55vh" overflow="auto">
        {renderDetails()}
      </Box>
    </Box>
  );
}

export default ViewChurchMember;